/**
 * **dev build 挂在 `window.DEV_TOOLS` 上的那套调试工具。**
 *
 * 评审：「在 dev build 中，在 window 上附加一个 `DEV_TOOLS` 变量，
 * 包含一系列调试工具，比如查看内部数据，覆写 BGM 参数等。」
 *
 * ## 为什么要有它
 *
 * 这个项目里"看内部数据"的需求一直都在，但以前只有两种做法：
 *
 * - 冒烟脚本用 `__MENU__.xxx` 一个个探（那些是给脚本用的，形状很随意）
 * - 手动在控制台里 `globalThis.__PIXI_APP__.stage.children[0]...` 摸进去
 *
 * 后者尤其糟糕：**摸进去的路径是渲染树的实现细节**，重构一次就全废，
 * 而且没人记得住。所以这里把"能把什么拿出来、能改什么"**列成一份接口**：
 * 在控制台敲 `DEV_TOOLS` 就能看到全部能力（浏览器会把对象展开成可点的树）。
 *
 * ## 三件事要分清
 *
 * | 分区 | 是给谁的 |
 * | --- | --- |
 * | `screens` / `room` / `settings` | 菜单系统的状态（栈、房间、设置） |
 * | `net` | 联机那一桌（建房/进房/离开/房主侧请求） |
 * | `music` | 动态音乐：快照、**覆写参数**、时钟、指挥器 |
 * | `game` / `data` | **内部数据**：当前对局屏的 `HotSeat`、视图、合法动作 |
 * | `render` | 渲染器、世界容器、背景情绪（色调/亮度/时间倍率） |
 *
 * ## ⚠️ 只在 dev 下存在
 *
 * `main.ts` 把它包在 `if (import.meta.env.DEV)` 里 —— 生产构建里连这段代码
 * 都没有（不是"藏起来了"）。**调试工具不许进产物**：它们能改 BGM 参数、
 * 能直接把视图打出来，那是给开发者的后门。
 *
 * ⚠️ `window.__MENU__` 仍然指向同一个对象：四个冒烟脚本在读它，
 * 而"给脚本用的把手"和"给人用的工具"本来就是同一批东西 —— 留个别名，
 * 免得为了改名去动四个脚本（新写的脚本一律用 `DEV_TOOLS`）。
 */

import type { EnergyFacts, EnergySnapshot } from '../audio/energy';
import type { ZenithBgmParams } from '../audio/score.zenith';

/**
 * **BGM 参数的覆写开关。**
 *
 * 评审要的"覆写 BGM 参数"就是它：`DEV_TOOLS.music.override({ isFinal: true })`
 * 之后，评分表那条线**停止**往指挥器推参数，你想听哪一档就听哪一档。
 * 不锁的话下一帧就被 `report()` 覆盖回去 —— 那种"改了一下就弹回去"的调试
 * 体验比没有还差。
 *
 * ```js
 * DEV_TOOLS.music.override({ isFinal: true, isDying: true, energy: 6 })
 * DEV_TOOLS.music.override({ energy: 12 })   // 叠加，不是替换
 * DEV_TOOLS.music.release()                  // 交还给评分表
 * ```
 */
export interface BgmOverride {
  /** 锁住了没有。锁住时评分表推什么都会被丢掉 */
  readonly active: boolean;
  /** 现在生效的那一份（覆写 + 之前的默认值） */
  readonly params: ZenithBgmParams;
  /** 叠加几个字段并推给指挥器。**自动上锁** */
  set(patch: Partial<ZenithBgmParams>): ZenithBgmParams;
  /** 交还给评分表（下一帧 `report()` 就会把它推回去） */
  release(): void;
}

export function createBgmOverride(
  port: { setParams(params: ZenithBgmParams): void },
  initial: ZenithBgmParams,
): BgmOverride {
  let locked = false;
  let params: ZenithBgmParams = { ...initial };

  return {
    get active() {
      return locked;
    },
    get params() {
      return { ...params };
    },
    set(patch) {
      locked = true;
      params = { ...params, ...patch };
      port.setParams(params);
      return { ...params };
    },
    release() {
      locked = false;
    },
  };
}

/**
 * 调试工具的形状。
 *
 * ⚠️ 字段一律**用函数**（`view()` 而不是 `view`）：这些数据是"此刻的"，
 * 存成属性就会看到一份过期的快照 —— 而调试时最容易得出的错误结论就是
 * "状态没变"。
 */
export interface DevTools {
  /** 版本与提交号（页脚显示的是同一份） */
  readonly build: { readonly version: string; readonly commit: string };
  /** 本机席位 id */
  readonly selfId: string;
  /** 当前昵称（没设过就是默认名）。冒烟脚本在读它 */
  nickname(): string;
  /** 评分表的快照。和 `music.snapshot()` 是同一个东西，留个短名字 */
  energy(): EnergySnapshot;
  /** 菜单：屏幕栈与切屏 */
  readonly screens: {
    readonly host: unknown;
    state(): unknown;
    go(id: string): void;
    push(id: string): void;
    /** 退一层（屏幕栈） */
    pop(): void;
    /** 开一个叠加层（设置那种） */
    overlay(id: string): void;
    closeOverlay(id?: string): void;
  };
  /** 设置（音量、昵称、UI 倍率） */
  readonly settings: unknown;
  /** 输入画像（`render/input`）：用来验证“从事件属性推断、没有模式” */
  readonly input: {
    traits(): unknown;
    /** 当前焦点的 id（走焦诊断用；没焦点就是 null） */
    focus(): unknown;
  };
  /** 房间（大厅模型：名单、游戏规则、准备状态） */
  readonly room: unknown;
  /** 联机那一桌。没进房时 `online()` 是 null */
  readonly net: {
    online(): unknown;
    connect(code: string): unknown;
    /**
     * 连接流程现在走到哪一步（`ui/connect.ts` 的相位）。
     *
     * 冒烟测试要它：`ready` 之前**不许**进房间页，那是这一轮加"正在连接"
     * 页的全部意义。
     */
    connectState(): unknown;
    leave(): void;
    /** 房主问某个 peer 要一次手牌顺序（联机抽牌对峙那条路） */
    askStealOrder(to: string): Promise<unknown>;
    /**
     * ★ **当前对局 Session 的现场**（协议层 + 房主内部状态）。
     *
     * 形状见 net/session.ts 的 S3SessionDebug：房主那侧带**完整 GameState**、
     * 动作流水（成功/被拒都有）、每个人最近推过的视图哈希、排着的定时器。
     * 没开局时是 null。
     */
    session(): unknown;
    /**
     * ★ **现在真正握着的信令中继 socket**（数组：url / readyState / open）。
     *
     * 两台机器都敲一下，比一比 open 的 URL 集合 —— 没交集就永远发现不了对方。
     * 想让两台机器用同一批：查询参数 relay= / localStorage 的 s3.relays /
     * 构建期 VITE_RELAY_URLS。
     */
    relays(): unknown;
  };
  /** 动态音乐 */
  readonly music: {
    /** 评分表的快照（energy / isDying / isFinal / 牌堆基线…） */
    snapshot(): EnergySnapshot;
    /** 命令器里**真的**参数 */
    params(): unknown;
    /** 压住 / 放行 `isDying`（抽牌动画期间用，见 `EnergyReporter.holdDying`） */
    holdDying(hold: boolean): void;
    /** 手动把一份局面喂给评分表（终局那种要打十回合的状态靠它复现） */
    report(facts: EnergyFacts): void;
    /** ★ 覆写参数（调试用，见 `BgmOverride`） */
    readonly override: BgmOverride;
    /** 小节时钟。`fireBar` / `fireBeat` 能手动敲一下 */
    readonly clock: unknown;
    /** BgmController（起播状态、音量）与底层指挥器 */
    readonly bgm: unknown;
    readonly port: unknown;
    readonly director: unknown;
    /**
     * **BGM 的加载读数。**
     *
     * 评审问过"每次刷新都要重新解码 bgm，能不能空间换时间"——
     * `initialMs` 是刷新到能出声的等待（只等当前那一个 cue），
     * `totalMs` 是后台补齐其余 cue 的时间，`pcmBytes` 是解出来的 PCM 体积
     * （= 那份"空间"）。见 `docs/dynamic-music.md` §6。
     */
    load(): unknown;
    /** 后台补完那一条链（`await` 它就等于"全部 cue 都解好了"） */
    warm(): Promise<void> | null;
  };
  /** 内部数据：当前对局屏 */
  readonly game: {
    /** 活着的对局屏（热座 / 联机）。它会自己挂进来、销毁时摘掉 */
    current(): unknown;
    /** 当前操作者看到的视图（`project()` 的输出） */
    view(): unknown;
    /** 合法动作清单（UI 的按钮就是从它来的） */
    options(): unknown;
    /** 直接执行一个动作（调试用，绕开 UI） */
    act(action: unknown): unknown;
    /** 表现层正在放的东西（抽牌动画）。没有对局屏时是 null */
    effects(): unknown;
    /**
     * 右下角动作按钮的现场（文案 / 变红没有 / 长按条涨到哪儿）。
     * 没有对局屏时是 null。见 `render/ui/button.ts` 的 `setConfirm`
     */
    buttons(): unknown;
    /**
     * ★ **当前对局的权威状态**（GameState 原样）。
     *
     * 热座来自 HotSeat.fullState；联机只有**房主**那一侧有，客户端返回 null。
     * 诊断“动作发了、画面不动”时先看它：窗口的 committed、手牌删没删、
     * resolution 里是哪一手、options 还剩什么，一眼就能对上 reducer。
     */
    state(): unknown;
    /**
     * ★ **桌面时序现场**：待结算窗口 / 抉择 / 排着的定时器 / 举着的牌 /
     * 手牌现在可不可打（handLive / handPlayable / handDragOut）。
     *
     * 它专治“看着亮了又回去”那类 bug —— 单独看视图看不出 handPlayable 掉了。
     */
    table(): unknown;
    /**
     * ★ **联机那一局的 Session 现场**（协议层 + 房主内部状态）。热座是 null。
     * 形状见 net/session.ts 的 S3SessionDebug。
     */
    session(): unknown;
  };
  /** 渲染层 */
  readonly render: {
    readonly renderer: unknown;
    readonly world: unknown;
    /** 背景的情绪：亮度 / 色调 / 时间倍率（见 `render/bg/mood.ts`） */
    mood(): unknown;
    /** 濒死层现在开着吗（评审报过"濒死效果不出现"） */
    dying(): boolean;
    /** 背景渲染档位（原样 / 像素化 / ASCII）与那块 FBO 的尺寸 */
    field(): unknown;
    /** 场铺在哪一块（画布逻辑坐标里的矩形）。窗口不是 16:10 时它比画布大 */
    quad(): unknown;
    /** 故障滤镜现在的状态（开着没有 / 强度 / 这一段还剩多久） */
    glitch(): unknown;
    /**
     * **打击**（`render/impact.ts`）现在的状态：冻结还剩多久、trauma、闪多大、
     * 留白掐到多低，以及**世界变换原样一份**。
     *
     * ⚠️ 冒烟只能靠读数验三件事：冻结真的把画面定住了、震动会精确归零
     * （拿打击前后两份世界变换比）、颜色的三项会回 0。
     */
    impact(): unknown;
    /**
     * 裂缝遮罩的现场读数：`strength` 之外还有**贴图里真的盖住了多少像素**。
     *
     * ⚠️ 必须量像素：`setDying()` 曾经每帧调一次 `begin()`，于是 `strength`
     * 一直是 1 而裂缝一个像素都没有 —— 只看强度那个数会**假绿**。
     */
    cracks(): unknown;
  };
  /** 一次把所有内部数据打出来（控制台里 `copy(DEV_TOOLS.dump())` 很方便） */
  /**
   * ★ **权威那一侧的现场**（热座的 HotSeat / 联机对局的房主 Session）。
   *
   * 一次给出：完整状态（state）、桌面时序（table）、协议与流水（session）。
   * 排查“动作发了、画面不对 / 牌又回去了”时，先敲它。
   */
  host(): { state: unknown; table: unknown; session: unknown };
  dump(): Record<string, unknown>;
}

/**
 * 把工具挂到 `window` 上。
 *
 * 返回值是"摘掉"的函数（测试与热重载用；正常运行时没人调它）。
 */
export function installDevTools(tools: DevTools): () => void {
  const target = globalThis as unknown as { DEV_TOOLS?: DevTools; __MENU__?: DevTools };
  target.DEV_TOOLS = tools;
  // ⚠️ 别名：四个冒烟脚本还在读 `__MENU__`。新代码一律用 `DEV_TOOLS`
  target.__MENU__ = tools;
  return () => {
    if (target.DEV_TOOLS === tools) delete target.DEV_TOOLS;
    if (target.__MENU__ === tools) delete target.__MENU__;
  };
}
